// src/pages/Jobs/cards/EstimatedTonnageCard.tsx
import React, { useEffect, useMemo } from "react";
import ProductPicker from "@/components/ProductPicker";

export type EstProd = {
  productType: string;
  value: number; // tons estimate
};

type Props = {
  rows: EstProd[];
  onChange: (rows: EstProd[]) => void;
  onTotal?: (sum: number) => void; // callback for live sum
};

export default function EstimatedTonnageCard({ rows, onChange, onTotal }: Props) {
  const total = useMemo(() => rows.reduce((a, r) => a + (Number(r.value) || 0), 0), [rows]);

  useEffect(() => {
    onTotal?.(total);
  }, [total, onTotal]);

  const update = (idx: number, next: Partial<EstProd>) => {
    const copy = rows.slice();
    copy[idx] = { ...copy[idx], ...next };
    onChange(copy);
  };

  const addRow = () => onChange([...rows, { productType: "", value: 0 }]);
  const removeRow = (idx: number) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="font-medium">Estimated Tonnage by Product</div>
        <div className="text-sm tabular-nums">
          Total: <span className="font-semibold">{total.toLocaleString()}</span> tons
        </div>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 gap-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
              <div className="md:col-span-2">
                <ProductPicker
                  value={r.productType}
                  onChange={(val: string) => update(i, { productType: val })}
                  productType={r.productType}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  className="input w-full text-right tabular-nums"
                  value={Number.isFinite(r.value) ? r.value : 0}
                  onChange={(e) => update(i, { value: Number(e.target.value || 0) })}
                  min={0}
                  step={1}
                />
                <button className="btn btn-secondary" onClick={() => removeRow(i)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
          <div>
            <button className="btn" onClick={addRow}>
              Add product
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
